import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { VideoQuic } from "./components/video/VideoQuic";
import { PRIVACY_PIPELINE_BUILD } from "./components/video/privacy";

type Feed = {
  id: string;
  tail: string;
  mountKey: number;
};

/** Matches FloatingAircraft card width on the pilot dash. */
const DASH_CARD_WIDTH = 433;
/** Matches VideoQuic height on the pilot dash. */
const DASH_VIDEO_HEIGHT = 290;
const MAX_FEEDS = 10;

declare const __webpack_hash__: string | undefined;

/**
 * Webpack's per-compile hash. Unlike PRIVACY_BUILD this cannot be forgotten —
 * it changes on every rebuild, so an unchanged hash after a code edit is proof
 * of a cached bundle rather than a code problem.
 */
const BUILD_HASH =
  typeof __webpack_hash__ === "string" ? __webpack_hash__.slice(0, 8) : "dev";

function hostsForTail(tail: string): string[] {
  return [`e${tail}-sim1.m4mga3.com`, `e${tail}-sim2.m4mga3.com`];
}

function newFeed(tail: string): Feed {
  return {
    id: `${tail}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    tail,
    mountKey: Date.now(),
  };
}

/**
 * /video-test harness — multi-feed QUIC video at pilot-dash panel size.
 */
export const VideoTest = () => {
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString()
  );
  const [draftTail, setDraftTail] = useState("");
  const [draftError, setDraftError] = useState(false);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  /** Independent privacy controls — remount feeds when either changes. */
  const [blurEnabled, setBlurEnabled] = useState(true);
  const [personDetectionEnabled, setPersonDetectionEnabled] = useState(true);
  const [chaosNote, setChaosNote] = useState("");
  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedFeedId && feeds.length > 0) {
      setSelectedFeedId(feeds[0].id);
    }
    if (selectedFeedId && !feeds.some((f) => f.id === selectedFeedId)) {
      setSelectedFeedId(feeds[0]?.id ?? null);
    }
  }, [feeds, selectedFeedId]);

  const selectedFeed = useMemo(
    () => feeds.find((f) => f.id === selectedFeedId) || null,
    [feeds, selectedFeedId]
  );

  /**
   * Copy number per feed, and how many copies each tail has. The same
   * aircraft can be added repeatedly, so cards need to be distinguishable.
   */
  const { copyIndexById, tailCounts } = useMemo(() => {
    const seen = new Map<string, number>();
    const byId = new Map<string, number>();
    feeds.forEach((feed) => {
      const next = (seen.get(feed.tail) || 0) + 1;
      seen.set(feed.tail, next);
      byId.set(feed.id, next);
    });
    return { copyIndexById: byId, tailCounts: seen };
  }, [feeds]);

  const feedLabel = useCallback(
    (feed: Feed) => {
      const total = tailCounts.get(feed.tail) || 1;
      const index = copyIndexById.get(feed.id) || 1;
      return total > 1 ? `MNA-${feed.tail} #${index}` : `MNA-${feed.tail}`;
    },
    [copyIndexById, tailCounts]
  );

  /** Split the input into tails, accepting space, comma or newline separators. */
  const parseTails = (value: string): string[] =>
    value.split(/[\s,]+/).filter(Boolean);

  const draftTails = parseTails(draftTail);
  const draftValid =
    draftTails.length > 0 && draftTails.every((t) => /^\d{4}$/.test(t));

  const handleDraftChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/^[\d\s,]*$/.test(value)) {
      setDraftTail(value);
      const tails = parseTails(value);
      setDraftError(
        tails.length > 0 && !tails.every((t) => /^\d{0,4}$/.test(t))
      );
    }
  };

  const remountAll = () => {
    setFeeds((prev) =>
      prev.map((f) => ({ ...f, mountKey: Date.now() + Math.random() }))
    );
  };

  const readPrivacyStatus = () => {
    try {
      const statusFn = (window as any).__mannaPrivacyStatus;
      if (typeof statusFn === "function") {
        return statusFn() as {
          processors: number;
          detectLoop: boolean;
          modelReady: boolean;
        };
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  const processingEnabled = blurEnabled || personDetectionEnabled;

  const setBlur = (checked: boolean) => {
    setBlurEnabled(checked);
    remountAll();
    window.setTimeout(() => {
      const status = readPrivacyStatus();
      setChaosNote(
        checked
          ? `Video blur ON${personDetectionEnabled ? " + detect" : ""} — ${JSON.stringify({
              detectLoop: status?.detectLoop,
              modelReady: status?.modelReady,
              processors: status?.processors,
            })}`
          : `Video blur OFF${personDetectionEnabled ? " (detect still on)" : ""}`
      );
    }, 750);
  };

  const setDetect = (checked: boolean) => {
    setPersonDetectionEnabled(checked);
    remountAll();
    window.setTimeout(() => {
      const status = readPrivacyStatus();
      setChaosNote(
        checked
          ? `Person detect ON${blurEnabled ? " + blur" : ""} — ${JSON.stringify({
              detectLoop: status?.detectLoop,
              modelReady: status?.modelReady,
              processors: status?.processors,
            })}`
          : `Person detect OFF${blurEnabled ? " (blur still on)" : ""}`
      );
    }, 750);
  };

  const addFeed = () => {
    if (!draftValid) {
      setDraftError(true);
      return;
    }
    const room = MAX_FEEDS - feeds.length;
    if (room <= 0) {
      setChaosNote(`Max ${MAX_FEEDS} feeds`);
      return;
    }
    // Duplicates are allowed on purpose: the same tail added twice opens two
    // independent transports and two privacy streams, which is how we load
    // test 10 feeds against a single available aircraft.
    const accepted = draftTails.slice(0, room);
    const added = accepted.map(newFeed);
    setFeeds((prev) => [...prev, ...added]);
    setSelectedFeedId(added[added.length - 1].id);
    setDraftError(false);
    const dropped = draftTails.length - accepted.length;
    if (added.length === 1) {
      const copies = feeds.filter((f) => f.tail === accepted[0]).length + 1;
      setChaosNote(
        copies > 1
          ? `Added MNA-${accepted[0]} (copy ${copies})`
          : `Added MNA-${accepted[0]}`
      );
      return;
    }
    setChaosNote(
      `Added ${added.length} feeds${dropped > 0 ? ` — ${dropped} skipped, max ${MAX_FEEDS}` : ""}`
    );
  };

  /** Clone the selected feed (or the last one) up to MAX_FEEDS. */
  const fillToMax = () => {
    const template = selectedFeed || feeds[feeds.length - 1];
    if (!template) {
      setChaosNote("Add one feed first, then Fill to 10");
      return;
    }
    const missing = MAX_FEEDS - feeds.length;
    if (missing <= 0) {
      setChaosNote(`Already at ${MAX_FEEDS} feeds`);
      return;
    }
    const clones: Feed[] = [];
    for (let i = 0; i < missing; i += 1) {
      clones.push(newFeed(template.tail));
    }
    setFeeds((prev) => [...prev, ...clones]);
    setChaosNote(
      `Filled to ${MAX_FEEDS} feeds with MNA-${template.tail} (+${missing})`
    );
  };

  const removeFeed = (id: string) => {
    setFeeds((prev) => prev.filter((f) => f.id !== id));
  };

  const runChaos = (kind: "fail" | "peek" | "circuit") => {
    if (!selectedFeed) {
      setChaosNote("Select a feed first");
      return;
    }
    if (!processingEnabled) {
      setChaosNote("Enable blur / AI processing first");
      return;
    }
    // Keyed by feed id, not tail, so duplicates of the same aircraft each
    // have their own hooks.
    const hooks = (window as any).__mannaPrivacy?.[selectedFeed.id];
    const label = feedLabel(selectedFeed);
    if (!hooks) {
      setChaosNote(`Privacy hooks not ready for ${label} — wait for frames`);
      return;
    }
    if (kind === "fail") {
      for (let i = 0; i < 5; i++) {
        hooks.forceFail("chaos_inject");
      }
      setChaosNote(`${label}: injected 5 failures — raw feed must stay`);
      return;
    }
    if (kind === "peek") {
      const peeking = hooks.peek();
      setChaosNote(
        `${label}: ${peeking ? "Peek ON (raw)" : "Peek OFF (blur)"}`
      );
      return;
    }
    setChaosNote(
      `${label}: ${hooks.isCircuitOpen() ? "Circuit OPEN" : "Circuit closed"}`
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        backgroundColor: "#0f1115",
        color: "#e8eaed",
        pb: 4,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backgroundColor: "#171a21",
          borderBottom: "1px solid #2a3140",
          px: 2.5,
          py: 1.75,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", letterSpacing: "-0.01em" }}>
              Video test
            </Typography>
            <Typography sx={{ color: "#8b95a8", fontSize: "0.78rem" }}>
              Same VideoQuic path as pilot dash · panels {DASH_CARD_WIDTH}×{DASH_VIDEO_HEIGHT}px
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                color: "#8b95a8",
                fontVariantNumeric: "tabular-nums",
                fontSize: "0.95rem",
              }}
            >
              {currentTime}
            </Typography>
            <Typography
              title="Build stamp. If this does not change after a rebuild, the page is cached."
              sx={{
                color: "#5c677a",
                fontFamily: "monospace",
                fontSize: "0.72rem",
              }}
            >
              {PRIVACY_PIPELINE_BUILD} · {BUILD_HASH}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr 1fr" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {/* Feeds */}
          <Box
            sx={{
              backgroundColor: "#1e2430",
              border: "1px solid #2a3140",
              borderRadius: 1.5,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            <Typography sx={{ color: "#8b95a8", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Feeds ({feeds.length}/{MAX_FEEDS})
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <Typography sx={{ mt: 1, fontWeight: 600, color: "#c5cddb" }}>MNA-</Typography>
              <TextField
                size="small"
                value={draftTail}
                onChange={handleDraftChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    addFeed();
                  }
                }}
                error={draftError}
                placeholder="2280 or 1680 1810 1840"
                helperText={
                  draftError ? "4 digits per tail, space separated" : " "
                }
                inputProps={{ inputMode: "numeric" }}
                sx={{
                  width: 260,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#12151c",
                    color: "#e8eaed",
                    "& fieldset": { borderColor: "#2a3140" },
                  },
                  "& .MuiFormHelperText-root": { color: draftError ? "#ff8a80" : "transparent" },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={addFeed}
                disabled={!draftValid || feeds.length >= MAX_FEEDS}
                sx={{ mt: 0.25, textTransform: "none", fontWeight: 600 }}
              >
                {draftTails.length > 1 ? `Add ${draftTails.length}` : "Add feed"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={fillToMax}
                disabled={feeds.length === 0 || feeds.length >= MAX_FEEDS}
                title="Clone the selected feed until there are 10"
                sx={{
                  mt: 0.25,
                  textTransform: "none",
                  borderColor: "#3a4558",
                  color: "#e8eaed",
                }}
              >
                Fill to {MAX_FEEDS}
              </Button>
            </Box>
            <Typography sx={{ color: "#5c677a", fontSize: "0.7rem" }}>
              Space-separated tails add several at once. The same tail can be
              added repeatedly — each copy is an independent transport and
              privacy stream.
            </Typography>
          </Box>

          {/* Processing — all streams */}
          <Box
            sx={{
              backgroundColor: "#1e2430",
              border: "1px solid #2a3140",
              borderRadius: 1.5,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            <Typography sx={{ color: "#8b95a8", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Processing (all streams)
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={blurEnabled}
                  color="primary"
                  onChange={(_, checked) => {
                    setBlur(checked);
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                    Video blur
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#8b95a8" }}>
                    Blur outside large cyan ROI (≈12–55% of feed); small reticle / no ring stays raw
                  </Typography>
                </Box>
              }
              sx={{ m: 0, alignItems: "flex-start", gap: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={personDetectionEnabled}
                  color="primary"
                  onChange={(_, checked) => {
                    setDetect(checked);
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                    Person detection
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#8b95a8" }}>
                    stephan-best boxes (works with or without blur)
                  </Typography>
                </Box>
              }
              sx={{ m: 0, alignItems: "flex-start", gap: 1 }}
            />
          </Box>

          {/* Chaos */}
          <Box
            sx={{
              backgroundColor: "#1e2430",
              border: "1px solid #2a3140",
              borderRadius: 1.5,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography sx={{ color: "#8b95a8", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Chaos {selectedFeed ? `· MNA-${selectedFeed.tail}` : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                disabled={!processingEnabled || !selectedFeed}
                onClick={() => runChaos("peek")}
                sx={{ textTransform: "none", borderColor: "#3a4558", color: "#e8eaed" }}
              >
                Peek
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                disabled={!processingEnabled || !selectedFeed}
                onClick={() => runChaos("fail")}
                sx={{ textTransform: "none" }}
              >
                Inject fail
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!processingEnabled || !selectedFeed}
                onClick={() => runChaos("circuit")}
                sx={{ textTransform: "none", borderColor: "#3a4558", color: "#e8eaed" }}
              >
                Circuit
              </Button>
            </Box>
            <Typography sx={{ fontSize: "0.75rem", color: chaosNote ? "#c5cddb" : "#5c677a", minHeight: 18 }}>
              {chaosNote || "Select a feed card, then run a chaos action"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {feeds.length === 0 ? (
        <Box sx={{ p: 6, textAlign: "center" }}>
          <Typography sx={{ color: "#8b95a8" }}>
            No feeds yet — enter a 4-digit tail and click Add feed.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            p: 2.5,
            justifyContent: "flex-start",
            alignContent: "flex-start",
          }}
        >
          {feeds.map((feed) => {
            const selected = feed.id === selectedFeedId;
            return (
              <Box
                key={feed.id}
                onClick={() => setSelectedFeedId(feed.id)}
                sx={{
                  width: DASH_CARD_WIDTH,
                  flex: `0 0 ${DASH_CARD_WIDTH}px`,
                  backgroundColor: "#171a21",
                  borderRadius: 1.5,
                  overflow: "hidden",
                  border: selected ? "2px solid #5b8cff" : "2px solid #2a3140",
                  cursor: "pointer",
                  boxShadow: selected ? "0 0 0 1px rgba(91,140,255,0.35)" : "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid #2a3140",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.92rem" }}>
                      {feedLabel(feed)}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#8b95a8",
                        fontSize: "0.68rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: DASH_CARD_WIDTH - 64,
                      }}
                    >
                      {hostsForTail(feed.tail).join(" · ")}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={`Remove ${feedLabel(feed)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFeed(feed.id);
                    }}
                    sx={{ color: "#8b95a8", "&:hover": { color: "#fff" } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ width: DASH_CARD_WIDTH, height: DASH_VIDEO_HEIGHT }}>
                  <VideoQuic
                    key={feed.mountKey}
                    ipAddresses={hostsForTail(feed.tail)}
                    uavId={feed.tail}
                    instanceKey={feed.id}
                    blurEnabled={blurEnabled}
                    personDetectionEnabled={personDetectionEnabled}
                    showDebug
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
